import Grid from '@material-ui/core/Grid';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import createStyles from '@material-ui/core/styles/createStyles';
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';
import deepmerge from 'deepmerge';
import React, { PureComponent } from 'react';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import messages from '../intl/messages';
import { AppState } from '../store/model';
import {
  getError,
  isLoading,
  isRejected,
  isSaviSelectIntl,
} from '../store/reducers/itinerary/selectors';
import { ArrangerNotes } from './ArrangerNotes';
import { ItineraryOptions } from './ItineraryOptions';
import { breakpointMobile } from './lab/breakpoints';
import { spacings } from './lab/spacings';
import { PageHeader } from './lab/TextGroup/PageHeader';
import { LinkExpired } from './LinkExpired';
import { TravellerInfo } from './TravellerInfo';
import { TripNotification } from './TripNotification';
import { TripTo } from './TripTo';

interface StateProps {
  loading: boolean;
  error: any;
  showItinerary: boolean;
  isSaviSelectIntl: boolean;
}

interface ContentProps
  extends StateProps,
    InjectedIntlProps,
    WithStyles<typeof styles> {}

class ContentBase extends PureComponent<ContentProps> {
  public render() {
    const {
      loading,
      error,
      intl: { formatMessage },
      showItinerary,
      isSaviSelectIntl,
      classes,
    } = this.props;

    return (
      <Grid item className={classes.container}>
        {loading ? (
          <PageHeader>{formatMessage(messages.common.loading)}</PageHeader>
        ) : error ? (
          <LinkExpired />
        ) : (
          <>
            <TripTo />
            <TripNotification />
            {showItinerary && (
              <>
                <ArrangerNotes />
                <TravellerInfo />
                <ItineraryOptions isSaviSelectIntl={isSaviSelectIntl} />
              </>
            )}
          </>
        )}
      </Grid>
    );
  }
}

// styled
const styles = (theme: Theme) => {
  const {
    page: { bottom: pageBottomSpacing },
  } = spacings(theme);
  return createStyles({
    container: deepmerge(
      {
        /**
         * The following flexbox styles is so that the LinkExpired component is centered vertically and horizontally
         */
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        alignItems: 'stretch',
        flexWrap: 'nowrap',
        width: '100%',
        maxWidth: 840,
        [breakpointMobile(theme)]: {
          maxWidth: '100%',
        },
      },
      pageBottomSpacing,
    ) as any,
  });
};
const mapStateToProps = (state: AppState) => {
  return {
    loading: isLoading(state),
    error: getError(state),
    showItinerary: !isRejected(state),
    isSaviSelectIntl: isSaviSelectIntl(state),
  };
};

const enhance = compose<ContentProps, {}>(
  connect(mapStateToProps),
  withStyles(styles),
  injectIntl,
);

/**
 * Page content
 *
 * Contains:
 * - Page header (e.g "Trip to Auckland")
 * - Trip notification (e.g. "Please review and select your preferred options or request changes")
 * - Arranger notes (e.g. "Notes from Jane: There aren't many flight options remaining")
 * - Itinerary options (e.g. list of segments (grouped by date) and options for each segment (e.g. Air/Hotel/Car))
 */
export const Content = enhance(ContentBase);

Content.displayName = 'Content';
